import { trackAnalyticsEvent } from 'util/analytics';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import { Event, useEventsApi, useSchoolsApi } from 'services/api';
import { ListEventsResponse } from 'services/api/apis/events/dto';
import { useIsFullWidth, useIsSsr, useProfile } from 'hooks';
import { Routes } from 'services/routes';
import { TicketHolder } from 'components/modals/event-check-in/types';
import { localNowIso } from '../../shared';
import { canOrganizeFeedEvent, partitionFeedEvents } from './feedEvents';

interface ReservedResponse {
  reservations: Array<{
    event: Event;
    receipt: { receiptId: string };
    checkedIn: boolean;
  }>;
}

export interface CheckInSheetState {
  event: Event;
  organizer: boolean;
  receiptId?: string;
  tickets?: TicketHolder[];
}

const hasTimeSlots = (event: Event): boolean =>
  (event.timeSlots?.length ?? 0) > 0;

const needsTicketPage = (event: Event): boolean =>
  (event.ticketTypes?.length ?? 0) > 0 ||
  (event.guestTicketTypes?.length ?? 0) > 0;

export const useConnectedFeed = (schoolSlug: string | null) => {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const schoolsApi = useSchoolsApi();
  const eventsApi = useEventsApi();
  const { profile } = useProfile();
  const isDesktop = useIsFullWidth();
  const isSsr = useIsSsr();
  const viewed = useRef(false);
  const now = useMemo(() => localNowIso(), []);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [sheet, setSheet] = useState<CheckInSheetState | null>(null);
  const [timeSlotEvent, setTimeSlotEvent] = useState<Event | null>(null);
  const [timeSlotOpen, setTimeSlotOpen] = useState(false);
  const [extraReceipts, setExtraReceipts] = useState<Record<string, string>>(
    {},
  );
  const [optimistic, setOptimistic] = useState<
    Record<string, { checkedInAt?: string; reserved?: boolean }>
  >({});

  useEffect(() => {
    if (isSsr || viewed.current) return;
    viewed.current = true;
    trackAnalyticsEvent({
      name: 'connected_dashboard_viewed',
      props: { layout: isDesktop ? 'desktop' : 'mobile' },
    });
  }, [isDesktop, isSsr]);

  const eventsQuery = useSWR<ListEventsResponse>(
    schoolSlug ? ['connected-dashboard/events', schoolSlug, now] : null,
    async () => {
      const res = await schoolsApi.getSchoolEvents(schoolSlug as string, {
        timeFilter: 'upcoming',
        now,
        windowDays: 8,
        pageSize: 50,
      });
      return res.success ? res.value : { count: 0, events: [], cursor: null };
    },
  );

  const reservedQuery = useSWR<ReservedResponse>(
    `/events/reserved?timeFilter=upcoming&now=${encodeURIComponent(now)}`,
  );

  const receiptByEvent = useMemo(() => {
    const map = new Map<string, string>();
    for (const reservation of reservedQuery.data?.reservations ?? []) {
      if (reservation.receipt?.receiptId)
        map.set(reservation.event.id, reservation.receipt.receiptId);
    }
    for (const [eventId, receiptId] of Object.entries(extraReceipts))
      if (receiptId) map.set(eventId, receiptId);
    return map;
  }, [extraReceipts, reservedQuery.data]);

  const { today, upcoming, hostingIds } = useMemo(() => {
    const reservations = new Map(
      (reservedQuery.data?.reservations ?? []).map(reservation => [
        reservation.event.id,
        reservation,
      ]),
    );
    const merged = (eventsQuery.data?.events ?? []).map(event => {
      const reservation = reservations.get(event.id);
      const extra = optimistic[event.id];
      return {
        ...event,
        reserved: Boolean(event.reserved || reservation || extra?.reserved),
        checkedIn: Boolean(
          event.checkedIn || reservation?.checkedIn || extra?.checkedInAt,
        ),
        checkedInAt: extra?.checkedInAt || event.checkedInAt,
      };
    });
    const parts = partitionFeedEvents(merged);
    const hosts = new Set<string>();
    for (const event of [...parts.today, ...parts.upcoming])
      if (canOrganizeFeedEvent(event, profile)) hosts.add(event.id);

    return { ...parts, hostingIds: hosts };
  }, [eventsQuery.data, optimistic, profile, reservedQuery.data]);

  const refresh = (): void => {
    void eventsQuery.mutate();
    void reservedQuery.mutate();
  };

  const openEvent = (event: Event): void => {
    void router.push(event.url);
  };

  const lookupReceiptId = async (
    eventId: string,
  ): Promise<string | undefined> => {
    const known = receiptByEvent.get(eventId);
    if (known) return known;
    const fresh = await reservedQuery.mutate();
    return fresh?.reservations?.find(
      reservation => reservation.event.id === eventId,
    )?.receipt?.receiptId;
  };

  const rememberReservation = (eventId: string, receiptId?: string): void => {
    if (receiptId)
      setExtraReceipts(current => ({ ...current, [eventId]: receiptId }));
    setOptimistic(current => ({
      ...current,
      [eventId]: { ...current[eventId], reserved: true },
    }));
    refresh();
  };

  // Match the event page: simple events reserve in place and open the QR
  // sheet, time slots open the slot picker, and priced/typed tickets go to
  // the reserve page. This never calls self check-in.
  const beginReservation = async (
    event: Event,
    options: { openSheetOnFailure: boolean },
  ): Promise<void> => {
    setLoadingId(event.id);
    try {
      const detail = await eventsApi.getEvent(event.id);
      if (!detail.success) {
        enqueueSnackbar('Could not open this event.', { variant: 'error' });
        if (options.openSheetOnFailure) setSheet({ event, organizer: false });
        return;
      }
      const full = detail.value;
      if (hasTimeSlots(full)) {
        setTimeSlotEvent(full);
        setTimeSlotOpen(true);
        return;
      }
      if (needsTicketPage(full)) {
        void router.push(Routes.reserveTickets(full.id));
        return;
      }

      const result = await eventsApi.reserveTickets(event.id, {
        source:
          typeof window !== 'undefined' && window.ReactNativeWebView
            ? 'native'
            : 'web',
        tickets: [
          {
            firstName: profile?.firstName || '',
            lastName: profile?.lastName || '',
            email: profile?.primaryEmail || profile?.emails?.[0] || '',
          },
        ],
      });
      if (!result.success) {
        enqueueSnackbar(result.error.message || 'Could not RSVP.', {
          variant: 'error',
        });
        if (options.openSheetOnFailure) {
          const receiptId = await lookupReceiptId(event.id);
          setSheet({ event: full, organizer: false, receiptId });
        }
        return;
      }
      const receiptId = result.value.receiptId;
      const tickets: TicketHolder[] = [
        {
          firstName: profile?.firstName || '',
          lastName: profile?.lastName || '',
          email: profile?.primaryEmail || profile?.emails?.[0] || '',
          ticketNumber: receiptId,
        },
      ];
      rememberReservation(event.id, receiptId);
      setSheet({ event: full, organizer: false, receiptId, tickets });
    } finally {
      setLoadingId(null);
    }
  };

  const openCheckIn = async (
    event: Event,
    source: 'happening_today' | 'upcoming',
  ): Promise<void> => {
    if (loadingId) return;
    const organizer = canOrganizeFeedEvent(event, profile);
    trackAnalyticsEvent({
      name: 'event_check_in_started',
      props: {
        event_id: event.id,
        role: organizer ? 'organizer' : 'attendee',
        source,
      },
    });

    if (organizer) {
      setSheet({ event, organizer: true });
      return;
    }

    const known = receiptByEvent.get(event.id);
    if (known) {
      setSheet({ event, organizer: false, receiptId: known });
      return;
    }

    setLoadingId(event.id);
    try {
      const receiptId = await lookupReceiptId(event.id);
      if (receiptId || event.reserved || event.checkedIn) {
        setSheet({ event, organizer: false, receiptId });
        return;
      }

      await beginReservation(event, { openSheetOnFailure: true });
    } finally {
      setLoadingId(null);
    }
  };

  const rsvp = async (event: Event): Promise<void> => {
    if (loadingId || event.reserved) return;
    trackAnalyticsEvent({
      name: 'event_rsvp_clicked',
      props: { event_id: event.id, source: 'upcoming' },
    });
    await beginReservation(event, { openSheetOnFailure: false });
  };

  const seeAllEvents = (): void => {
    trackAnalyticsEvent({
      name: 'see_all_events_clicked',
      props: { source: 'upcoming' },
    });
    void router.replace(
      {
        pathname: router.pathname,
        query: { ...router.query, tab: 'events' },
      },
      undefined,
      { shallow: true },
    );
  };

  const closeSheet = (): void => {
    setSheet(null);
    refresh();
  };

  const closeTimeSlot = (): void => {
    setTimeSlotOpen(false);
  };

  const completeTimeSlot = (): void => {
    if (timeSlotEvent) rememberReservation(timeSlotEvent.id);
    else refresh();
  };

  return {
    today,
    upcoming,
    hostingIds,
    loading:
      Boolean(schoolSlug) && !eventsQuery.data && eventsQuery.isValidating,
    loadingId,
    sheet,
    timeSlotEvent,
    timeSlotOpen,
    openEvent,
    openCheckIn,
    rsvp,
    seeAllEvents,
    closeSheet,
    closeTimeSlot,
    completeTimeSlot,
  };
};
